import classNames from 'classnames';
import { useTransactionAiMetadata } from 'contexts/AIMetadataProvider/useTransactionAiMetadata';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Transaction } from 'types/transactions.types';
import { resolveTransactionCounterpartyName } from 'utils/banks/banks.helpers';
import formatMoney from 'utils/formatMoney';

const TransactionInformation: FC<{
  transaction: Transaction;
}> = ({ transaction }) => {
  const {
    t,
    i18n: { exists },
  } = useTranslation();
  const [communicationCollapsed, collapseCommunication] = useState(true);

  const {
    currency,
    amount,
    transactionType,
    valueDate,
    executionDate,
    counterPartyNumber,
  } = transaction;

  const originalCommunication =
    transaction?.originalCommunication || transaction?.communication;

  const _transactionType =
    transaction.provider === 'credit-card'
      ? t('transactions.type.credit_card')
      : (exists(`payments.filters.${transactionType}`) &&
          t(`payments.filters.${transactionType}`)) ||
        transactionType ||
        t('payments.unknown_payment_type');

  useTransactionAiMetadata(transaction);

  return (
    <>
      <div>
        <div className="flex items-center justify-between text-xl">
          <span className="font-semibold">
            {resolveTransactionCounterpartyName(transaction)}
          </span>

          <span className="font-bold">{formatMoney(amount, currency)}</span>
        </div>

        <div className="flex items-center justify-between font-semibold text-primary-700 text-lg mb-3">
          <span>{_transactionType}</span>
          {!!(valueDate || executionDate) && (
            <span>
              {dayjs(valueDate?.toString() || executionDate?.toString()).format(
                'll',
              )}
            </span>
          )}
        </div>

        {counterPartyNumber ? (
          <div className="mb-3">
            <p className="text-sm text-primary-500">
              {t('transaction.account_number')}
            </p>

            <div>
              <span className={'text-lg font-semibold text-primary-700'}>
                {counterPartyNumber}
              </span>
            </div>
          </div>
        ) : null}

        {originalCommunication ? (
          <div>
            <p className="text-sm text-primary-500">
              {t('transactions.cells.communication')}
            </p>

            <div>
              <span
                className={classNames('text-lg font-semibold text-primary-700')}
              >
                {originalCommunication.length >= 100 && communicationCollapsed
                  ? `${originalCommunication.slice(0, 50)}...`
                  : originalCommunication}
              </span>

              {originalCommunication.length >= 100 ? (
                <button
                  type="button"
                  className="underline text-bank ml-1"
                  onClick={() => collapseCommunication((prev) => !prev)}
                >
                  {communicationCollapsed ? t('show_more') : t('show_less')}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default TransactionInformation;
